import React, { useCallback, useState } from 'react'

import { formatDistanceToNow } from 'date-fns'
import Link from 'next/link'
import { LuFileSearch, LuFileText } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import { CompactThumbnail } from '@/components/common/Attachment/Thumbnail'
import Avatar from '@/components/common/Avatar'
import Checkbox from '@/components/common/Checkbox'
import { AvatarSkeleton, Skeleton } from '@/components/common/Skeleton'
import { getCompanyName } from '@/components/library-v2/companies/lib'
import { CompanyAvatar } from '@/components/library-v2/companies/Table/cells'
import { getPersonFullName } from '@/components/library-v2/people/lib'
import { PersonAvatar } from '@/components/library-v2/people/Table/cells'
import { PendingBadge } from '@/components/library-v2/ui/Badge'
import { ConfirmEntitiesDialog } from '@/components/library-v2/ui/ConfirmDialog'
import { Placeholder } from '@/components/library-v2/ui/Placeholder'

import { cleanFileName } from '@/helpers/file'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'
import type {
  Entity,
  EntityCompany,
  EntityPerson,
  EntityType,
} from '@/types/library/Entity'

const DEFAULT_AVATAR_SIZE = 28

// region: entity components

export const getEntityLink = (entity: Entity, activeOrgId: UUID | '') => {
  const entityType = entity.entity_type
  switch (entityType) {
    case 'company':
      return `/orgs/${activeOrgId}/library/companies/${entity.id}`
    case 'person':
      return `/orgs/${activeOrgId}/library/people/${entity.id}`
    case 'agency':
      return `/orgs/${activeOrgId}/library/agencies/${entity.id}`
    case 'catalog_entry':
      return `/orgs/${activeOrgId}/library/catalog/entries/${entity.id}`
    case 'file':
      return `/orgs/${activeOrgId}/library/files/${entity.id}`
    case 'department':
      return `/orgs/${activeOrgId}/library/departments/${entity.id}`
    default:
      const exhaustiveCheck: never = entityType
      return exhaustiveCheck
  }
}

export const getEntityName = (entity: Entity) => {
  const entityType = entity.entity_type
  switch (entityType) {
    case 'company':
      return getCompanyName(entity as EntityCompany)
    case 'person':
      return getPersonFullName(entity as EntityPerson, false)
    case 'agency':
      return entity.name
    case 'catalog_entry':
      return entity.name
    case 'file':
      // TODO: add file name
      return 'File'
    case 'department':
      return entity.name
    default:
      const exhaustiveCheck: never = entityType
      return exhaustiveCheck
  }
}

export const getEntityLabel = (entity: Entity) => {
  const entityType = entity.entity_type
  switch (entityType) {
    case 'company':
      return 'Company'
    case 'person':
      return 'Person'
    case 'agency':
      return 'Agency'
    case 'catalog_entry':
      return 'Catalog Entry'
    case 'file':
      return 'Library File'
    case 'department':
      return 'Department'
    default:
      const exhaustiveCheck: never = entityType
      return exhaustiveCheck
  }
}

/**
 * Returns singular or plural label for an entity type
 */

export const ENTITY_TYPE_LABELS: Record<
  EntityType,
  { single: string; plural: string }
> = {
  company: { single: 'company', plural: 'companies' },
  person: { single: 'person', plural: 'people' },
  agency: { single: 'agency', plural: 'agencies' },
  catalog_entry: { single: 'catalog entry', plural: 'catalog entries' },
  file: { single: 'file', plural: 'files' },
  department: { single: 'department', plural: 'departments' },
}

export const getEntityTypeLabel = (
  entity: Entity | undefined,
): { single: string; plural: string } => {
  if (!entity) {
    return { single: 'item', plural: 'items' }
  }
  return ENTITY_TYPE_LABELS[entity.entity_type]
}

interface EntityAvatarProps {
  entity: Entity
  size?: number
}

export const EntityAvatar: React.FC<EntityAvatarProps> = ({
  entity,
  size = DEFAULT_AVATAR_SIZE,
}) => {
  switch (entity.entity_type) {
    case 'company':
      return (
        <CompanyAvatar
          company={entity as EntityCompany}
          width={size}
          height={size}
        />
      )
    case 'person':
      return (
        <PersonAvatar
          person={entity as EntityPerson}
          width={size}
          height={size}
        />
      )
    default:
      return (
        <Avatar
          src={undefined}
          name={getEntityName(entity)}
          width={size}
          height={size}
        />
      )
  }
}

interface EntityRelationshipProps {
  entity: Entity
  avatarSize?: number
  selectable?: boolean
  selected?: boolean
  onSelectChange?: (selected: boolean) => void
}

export const EntityRelationship: React.FC<EntityRelationshipProps> = ({
  entity,
  avatarSize = DEFAULT_AVATAR_SIZE,
  selectable = false,
  selected = false,
  onSelectChange,
}) => {
  const { activeOrgId } = useActiveOrgId()

  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false)

  const handleOpenConfirmDialog = useCallback(() => {
    setConfirmDialogOpen(true)
  }, [setConfirmDialogOpen])

  const handleSelectClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      if (selectable) {
        onSelectChange?.(!(selected ?? false))
      }
    },
    [onSelectChange, selected, selectable],
  )

  return (
    <div className="group/entity-relationship-container flex items-center justify-between gap-3 ">
      {confirmDialogOpen && (
        <ConfirmEntitiesDialog
          entities={[entity]}
          open={confirmDialogOpen}
          setOpen={setConfirmDialogOpen}
        />
      )}
      <div
        className="flex items-center gap-3 truncate"
        data-selected={selected}
      >
        <div
          className={twMerge(
            'relative flex items-center gap-2 group/entity-relationship-avatar',
          )}
        >
          <button
            data-selected={selected}
            style={{ width: avatarSize, height: avatarSize }}
            className={twMerge(
              'absolute inset-0 flex items-center justify-center rounded-full bg-primary-200 dark:bg-primary-800/70',
              selectable
                ? 'opacity-0 group-hover/entity-relationship-avatar:opacity-100 data-[selected=true]:opacity-100 z-10'
                : 'opacity-0 pointer-events-none z-0',
            )}
            type="button"
            onClick={handleSelectClick}
          >
            <Checkbox
              id={entity.id}
              name={entity.id}
              checked={selected}
              childrenOnly={true}
              readOnly={true}
              className="bg-white dark:bg-primary-800"
            />
          </button>
          <div
            data-selected={selected}
            className={twMerge(
              'flex items-center gap-2',
              selectable
                ? 'group-hover/entity-relationship-avatar:opacity-0 data-[selected=true]:opacity-0'
                : '',
            )}
          >
            <EntityAvatar entity={entity} size={avatarSize} />
          </div>
        </div>
        <Link
          href={getEntityLink(entity, activeOrgId)}
          className="flex flex-1 items-center gap-3 group/entity-relationship truncate"
        >
          <div className="min-w-0 flex-1 truncate">
            <div className="flex items-center gap-2 min-w-0 truncate">
              <span className="font-medium text-primary-900 dark:text-primary-100 group-hover/entity-relationship:text-blue-600 dark:group-hover/entity-relationship:text-blue-400 text-sm text-left cursor-pointer truncate">
                {getEntityName(entity)}
              </span>
            </div>
            <div className="text-[13px] text-primary-500 dark:text-primary-400 capitalize">
              {getEntityLabel(entity)}
            </div>
          </div>
        </Link>
      </div>
      {!entity.confirmed && (
        <PendingBadge label="Confirm" onClick={handleOpenConfirmDialog} />
      )}
    </div>
  )
}

interface EntityRelationshipSkeletonProps {
  avatarSize?: number
}

export const EntityRelationshipSkeleton: React.FC<
  EntityRelationshipSkeletonProps
> = ({ avatarSize = DEFAULT_AVATAR_SIZE }) => {
  return (
    <div className="flex items-center gap-3">
      <AvatarSkeleton width={avatarSize} height={avatarSize} noName={true} />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="w-3/4 h-3" />
        <Skeleton className="w-1/2 h-[10px]" />
      </div>
    </div>
  )
}

// endregion: entity components

// region: entity file components

interface EntityFileRelationshipProps {
  file: File
  thumbnailSize?: number
}

export const EntityFileRelationship: React.FC<EntityFileRelationshipProps> = ({
  file,
  thumbnailSize = 20,
}) => {
  const { activeOrgId } = useActiveOrgId()
  return (
    <Link
      href={`/orgs/${activeOrgId}/library/files/${file.id}`}
      className={twMerge(
        'group/entity-file-relationship flex items-center gap-3 p-3',
        'rounded-lg border border-primary-200 dark:border-primary-800',
        'bg-white dark:bg-primary-900/50',
        'hover:bg-primary-50 dark:hover:bg-primary-900',
        'hover:border-primary-300 dark:hover:border-primary-700',
        'hover:shadow-sm hover:shadow-primary-900/5 dark:hover:shadow-black/20',
        'transition-all duration-150',
      )}
    >
      <div className="flex-shrink-0 flex items-center justify-center bg-primary-100 dark:bg-primary-800 rounded-md w-10 h-10 ring-1 ring-primary-200/70 dark:ring-primary-700/50">
        <CompactThumbnail name={file.name} size={thumbnailSize} />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium text-primary-900 dark:text-primary-100 truncate group-hover/entity-file-relationship:text-blue-600 dark:group-hover/entity-file-relationship:text-blue-400 transition-colors duration-150">
          {cleanFileName(file.name)}
        </span>
        {file.entity_file && (
          <span className="text-[13px] text-primary-500 dark:text-primary-400">
            {`Extracted ${formatDistanceToNow(new Date(file.entity_file.created_at), { addSuffix: true })}`}
          </span>
        )}
      </div>
    </Link>
  )
}

const RelatedFileSkeleton: React.FC = () => {
  return (
    <div
      className={twMerge(
        'flex items-center gap-3 p-3',
        'rounded-lg border border-primary-200 dark:border-primary-800',
        'bg-white dark:bg-primary-900/50',
      )}
    >
      <Skeleton className="w-10 h-10 rounded-lg flex-shrink-0" />
      <div className="flex flex-col gap-1.5 min-w-0 flex-1">
        <Skeleton className="w-3/4 h-4" />
        <Skeleton className="w-1/3 h-3" />
      </div>
    </div>
  )
}

const DEFAULT_EMPTY_STATE_DESCRIPTION =
  'Related files will appear here once they are processed.'

interface RelatedFilesEmptyStateProps {
  title?: string
  description?: string
  action?: React.ReactNode
}

export const RelatedFilesEmptyState: React.FC<RelatedFilesEmptyStateProps> = ({
  title = 'No related files found',
  description = DEFAULT_EMPTY_STATE_DESCRIPTION,
  action,
}) => {
  return (
    <Placeholder className="py-8 px-6 rounded-lg">
      <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-800 mb-4">
        <LuFileSearch
          size={24}
          className="text-primary-500 dark:text-primary-400"
        />
      </div>
      <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100 mb-1">
        {title}
      </h3>
      <p className="text-sm text-primary-500 dark:text-primary-400 max-w-xs">
        {description}
      </p>
      {action && <div className="mt-4">{action}</div>}
    </Placeholder>
  )
}

interface RelatedFilesProps {
  files: File[] | undefined
  isLoading: boolean
  className?: string
  emptyStateAction?: React.ReactNode
  emptyStateDescription?: string
}

export const RelatedFiles: React.FC<RelatedFilesProps> = ({
  files,
  isLoading,
  className,
  emptyStateAction,
  emptyStateDescription = DEFAULT_EMPTY_STATE_DESCRIPTION,
}) => {
  const fileCount = files?.length ?? 0

  return (
    <div className={twMerge('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-50 dark:bg-blue-900/30">
            <LuFileText
              size={16}
              className="text-blue-600 dark:text-blue-400"
            />
          </div>
          <div className="flex flex-col">
            <h2 className="text-sm font-semibold text-primary-900 dark:text-primary-100">
              Related Files
            </h2>
            {!isLoading ? (
              <p className="text-[13px] text-primary-500 dark:text-primary-400">
                {fileCount === 0
                  ? 'No files found'
                  : `${fileCount} file${fileCount === 1 ? '' : 's'} found`}
              </p>
            ) : (
              <Skeleton className="w-24 h-2.5 my-[5px]" />
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-2">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <RelatedFileSkeleton key={index} />
          ))
        ) : files && files.length > 0 ? (
          files.map((file) => (
            <EntityFileRelationship key={file.id} file={file} />
          ))
        ) : (
          <RelatedFilesEmptyState
            action={emptyStateAction}
            description={emptyStateDescription}
          />
        )}
      </div>
    </div>
  )
}

// endregion: entity file components
